import i18n from '../i18n';

/**
 * Device timezone offset for API payloads (minutes east of UTC).
 * Example: IST → 330. JS `Date.getTimezoneOffset()` is the opposite sign
 * (UTC − local), so we negate it to match .NET / existing app convention.
 */
const getTimezoneOffsetMinutes = (): number => -new Date().getTimezoneOffset();

/**
 * Format a date to a human-readable string
 * @param {Date|string|number} date
 * @param {string} [format='MMM DD, YYYY']
 * @returns {string}
 */
const formatDate = (
  date: Date | string | number,
  format = 'MMM DD, YYYY',
): string => {
  // Use parseToDate for correct UTC → local conversion. The old code
  // stripped the time/timezone and built a local-midnight Date from the
  // date part only, which showed the wrong date near UTC midnight.
  const d = date instanceof Date ? date : parseToDate(date);
  if (!d || isNaN(d.getTime())) return '';

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const fullMonths = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const day = d.getDate();
  const month = d.getMonth();
  const year = d.getFullYear();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  const pad = (n: number): string => String(n).padStart(2, '0');
  const h12 = hours % 12 || 12;
  const ampm = hours < 12 ? 'AM' : 'PM';

  return format.replace(/MMMM|MMM|MM|DD|D|YYYY|YY|HH|hh|mm|ss|A/g, (match) => {
    switch (match) {
      case 'MMMM': return fullMonths[month];
      case 'MMM': return months[month];
      case 'MM': return pad(month + 1);
      case 'DD': return pad(day);
      case 'D': return String(day);
      case 'YYYY': return String(year);
      case 'YY': return String(year).slice(-2);
      case 'HH': return pad(hours);
      case 'hh': return pad(h12);
      case 'mm': return pad(minutes);
      case 'ss': return pad(seconds);
      case 'A': return ampm;
      default: return match;
    }
  });
};

/** UI display pattern for Date of Birth (e.g. Aug 05, 2000). */
const DOB_DISPLAY_FORMAT = 'MMM DD, YYYY';

/** Display Date of Birth as Month Day, Year across the app (UI only). */
const formatDateOfBirth = (date: Date | string | number | null | undefined): string => {
  if (date == null || date === '') return '';
  // DOB is a calendar date, so drop any time component first: a value like
  // "2000-08-05T00:00:00" read as an instant rolls back a day west of UTC.
  return formatDate(toDateOnlyString(date) || date, DOB_DISPLAY_FORMAT);
};

/**
 * Calendar date → `YYYY-MM-DD` using **local** year/month/day.
 * DOB is a calendar date, not an instant — never use `toISOString()` here
 * (UTC conversion shifts the day in non-UTC timezones).
 */
const toDateOnlyString = (
  value: Date | string | number | null | undefined,
): string => {
  if (value == null || value === '') return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    // Prefer the YYYY-MM-DD calendar prefix so we never Date-parse a DOB
    // instant and shift the day by timezone. Covers date-only strings,
    // "2000-08-05T00:00:00…", and legacy toISOString() DOB values.
    const datePrefix = trimmed.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePrefix)) {
      return datePrefix;
    }
  }

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a calendar date string as local midnight.
 * - `YYYY-MM-DD` → local date (no UTC interpretation)
 * - other strings → native `Date` parse (prefer `toDateOnlyString` first
 *   for DOB so the YYYY-MM-DD prefix is preserved)
 */
const parseDateOnlyString = (
  value: string | null | undefined,
): Date | null => {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, day] = raw.split('-').map(Number);
    const d = new Date(y, m - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const RELATIVE_TIME_PATTERN =
  /^(just now|\d+\s+seconds? ago|\d+\s+minutes? ago|\d+\s+hours? ago|\d+\s*(sec|secs|min|mins|hr|hrs|h|d|day|days|wk|wks|mo|mos|yr|yrs)\s*ago|yesterday)$/i;

/**
 * How to read a backend timestamp that carries no timezone designator.
 *
 * - `'utc'` — the value is UTC. Conversation endpoints behave this way
 *   (see `conversationMapper.parseUtcTimestamp`).
 * - `'local'` — the API already converted the value to the caller's timezone
 *   from the `timezoneOffset` we send on the request. Feed, group-post,
 *   comment and notification reads all send it (`withFeedTimezoneOffset`).
 *   Appending `Z` to those strings applies the offset a second time, which
 *   pushes recent items into the future and collapses them to "Just now".
 */
type NaiveTimestampZone = 'utc' | 'local';

/** `YYYY-MM-DD[T ]HH:mm[:ss[.fraction]]` with no timezone designator. */
const NAIVE_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?$/;

const buildTimestamp = (
  parts: RegExpMatchArray,
  naiveZone: NaiveTimestampZone,
): Date | null => {
  const [, year, month, day, hour, minute, second, fraction] = parts;
  const y = Number(year);
  const mo = Number(month) - 1;
  const d = Number(day);
  const h = Number(hour);
  const mi = Number(minute);
  const s = second ? Number(second) : 0;
  // .NET returns up to 7 fractional digits; JS only takes milliseconds.
  const ms = fraction ? Number(fraction.slice(0, 3).padEnd(3, '0')) : 0;

  const built =
    naiveZone === 'utc'
      ? new Date(Date.UTC(y, mo, d, h, mi, s, ms))
      : new Date(y, mo, d, h, mi, s, ms);
  return isNaN(built.getTime()) ? null : built;
};

const parseToDate = (
  date: Date | string | number,
  naiveZone: NaiveTimestampZone = 'utc',
): Date | null => {
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof date === 'number') {
    // Support both seconds and milliseconds epoch values.
    const ms = date < 1e12 ? date * 1000 : date;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  const raw = String(date).trim();
  if (!raw) return null;

  // Already a relative string from the API / prior formatting.
  if (RELATIVE_TIME_PATTERN.test(raw)) {
    return null;
  }

  let normalized = raw;

  // Detect whether this is a date-only string (e.g. "2000-08-05") or a
  // full timestamp with a time component. Date-only strings are calendar
  // dates (DOB) and are always LOCAL — reading them as UTC would shift the
  // day in non-UTC timezones. Timestamps follow `naiveZone`.
  const hasTimeComponent = /[\sT]\d{2}:\d{2}/.test(normalized);
  const isDateOnly = !hasTimeComponent;

  // DD-MM-YYYY[ HH:mm:ss] → YYYY-MM-DDTHH:mm:ss
  if (/^\d{2}-\d{2}-\d{4}/.test(normalized)) {
    const parts = normalized.split(/[- :T]/);
    if (parts.length >= 3) {
      normalized = `${parts[2]}-${parts[1]}-${parts[0]}${
        parts[3] ? `T${parts[3]}` : ''
      }${parts[4] ? `:${parts[4]}` : ''}${parts[5] ? `:${parts[5]}` : ''}`;
    }
  } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(normalized)) {
    // YYYY-MM-DD HH:mm:ss → ISO-like
    normalized = normalized.replace(' ', 'T');
  } else if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(normalized)) {
    // M/D/YYYY[ h:mm:ss AM/PM] e.g. "8/7/2026 5:22:01 AM"
    const m = normalized.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i,
    );
    if (m) {
      let hours = m[4] != null ? parseInt(m[4], 10) : 0;
      const minutes = m[5] != null ? parseInt(m[5], 10) : 0;
      const seconds = m[6] != null ? parseInt(m[6], 10) : 0;
      const ampm = (m[7] || '').toUpperCase();
      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      const year = parseInt(m[3], 10);
      const month = parseInt(m[1], 10) - 1;
      const day = parseInt(m[2], 10);
      const parsed =
        naiveZone === 'utc'
          ? new Date(Date.UTC(year, month, day, hours, minutes, seconds))
          : new Date(year, month, day, hours, minutes, seconds);
      if (!isNaN(parsed.getTime())) return parsed;
    }
  }

  // Date-only strings (DOB, calendar dates) → parse as LOCAL, never UTC.
  if (isDateOnly && /^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const [y, mo, dy] = normalized.split('-').map(Number);
    const d = new Date(y, mo - 1, dy);
    if (!isNaN(d.getTime())) return d;
  }

  const hasExplicitZone = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(normalized);

  // Timestamp with no timezone designator → read it in the caller's declared
  // zone instead of letting the engine guess.
  if (hasTimeComponent && !hasExplicitZone) {
    const isoParts = normalized.match(NAIVE_TIMESTAMP_PATTERN);
    if (isoParts) {
      const built = buildTimestamp(isoParts, naiveZone);
      if (built) return built;
    }

    // Non-ISO shapes the engine can still parse (it treats them as local).
    if (naiveZone === 'utc') {
      const dUtc = new Date(`${normalized}Z`);
      if (!isNaN(dUtc.getTime())) return dUtc;
    }
  }

  // Fallback: native parse (handles strings with explicit timezone, and
  // date-only strings that should be local).
  const d = new Date(normalized);
  if (!isNaN(d.getTime())) return d;

  return null;
};

/**
 * Relative / absolute timestamp label used across feed, comments, chat and
 * notifications.
 *
 *   < 1s              Just Now
 *   1–59s             X second(s) ago
 *   1–59 min          X minute(s) ago
 *   1–24 hours        X hour(s) ago
 *   > 24 hours        MMM D, YYYY · h:mm AM/PM  (viewer local timezone)
 *
 * `style` is accepted for call-site compatibility; both styles use this sequence.
 * Pass `naiveZone: 'local'` for feed/comment/notification reads that send
 * `timezoneOffset`. Conversation timestamps are UTC instants (`Date` / epoch).
 * `absolute: 'timeOnly'` omits the date after 24 hours (chat message bubbles).
 */
const timeAgo = (
  date: Date | string | number,
  _style: 'default' | 'short' = 'default',
  naiveZone: NaiveTimestampZone = 'utc',
  absolute: 'dateTime' | 'timeOnly' = 'dateTime',
): string => {
  if (typeof date === 'string' && RELATIVE_TIME_PATTERN.test(date.trim())) {
    const trimmed = date.trim();
    if (/^just now$/i.test(trimmed)) return 'Just Now';
    return trimmed;
  }

  const d = parseToDate(date, naiveZone);
  if (!d) {
    return typeof date === 'string' ? date : '';
  }

  return formatRelativeTimestampFromDate(d, Date.now(), absolute);
};

const DAY_MS = 24 * 60 * 60 * 1000;

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** `Aug 17, 2026 · 8:08 PM` in the viewer's local timezone. */
const formatAbsoluteLocalDateTime = (date: Date): string =>
  `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} · ${formatTimeFromUtc(date)}`;

const formatRelativeTimestampFromDate = (
  date: Date,
  nowMs: number = Date.now(),
  absolute: 'dateTime' | 'timeOnly' = 'dateTime',
): string => {
  const diffMs = nowMs - date.getTime();
  if (diffMs < 1000) return 'Just Now';

  if (diffMs <= DAY_MS) {
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) {
      return diffSec === 1 ? '1 second ago' : `${diffSec} seconds ago`;
    }
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) {
      return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
    }
    const diffHour = Math.floor(diffMin / 60);
    return diffHour === 1 ? '1 hour ago' : `${diffHour} hours ago`;
  }

  return absolute === 'timeOnly'
    ? formatTimeFromUtc(date)
    : formatAbsoluteLocalDateTime(date);
};

/**
 * Check if a date falls on the viewer's local calendar today.
 */
const isToday = (
  date: Date | string | number,
  naiveZone: NaiveTimestampZone = 'utc',
): boolean => {
  const d = date instanceof Date ? date : parseToDate(date, naiveZone);
  if (!d) return false;
  const today = new Date();
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
};

/**
 * Get start of day
 * @param {Date} [date=new Date()]
 * @returns {Date}
 */
const startOfDay = (date: Date | string | number = new Date()): Date => {
  const parsed = date instanceof Date ? date : parseToDate(date, 'utc');
  const d = parsed ? new Date(parsed.getTime()) : new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get end of day
 * @param {Date} [date=new Date()]
 * @returns {Date}
 */
const endOfDay = (date: Date | string | number = new Date()): Date => {
  const parsed = date instanceof Date ? date : parseToDate(date, 'utc');
  const d = parsed ? new Date(parsed.getTime()) : new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Locale-aware chat list timestamp (device locale, no forced 12-hour en-US).
 */
const formatChatTime = (
  timestamp: number | string | Date = Date.now(),
): string => {
  const d = timestamp instanceof Date ? timestamp : parseToDate(timestamp, 'utc');
  if (!d) return '';
  return d
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .toLowerCase();
};

/**
 * Conversation-list timestamp, read in the viewer's timezone:
 *
 *   Today              → clock time, e.g. "9:05 AM"
 *   Yesterday          → "Yesterday"
 *   2–6 days back      → weekday, e.g. "Tue"
 *   Current year       → localized short date, e.g. "Aug 12"
 *   Previous year+     → localized date with year, e.g. "Dec 18, 2025"
 *
 * `timestamp` is a UTC instant (epoch ms, Date, or a naive UTC string).
 */
const formatConversationTime = (
  timestamp: number | string | Date = Date.now(),
  /** Reference point for the calendar comparison; defaults to the current time. */
  reference: number | string | Date = Date.now(),
): string => {
  const d = timestamp instanceof Date ? timestamp : parseToDate(timestamp, 'utc');
  if (!d) return '';

  const now =
    (reference instanceof Date ? reference : parseToDate(reference, 'utc')) ??
    new Date();
  const diffDays = Math.round(
    (startOfDay(now).getTime() - startOfDay(d).getTime()) / 86_400_000,
  );

  if (diffDays <= 0) {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (diffDays === 1) {
    // The weekday and date branches localize through Intl; this word cannot,
    // so it comes from the same catalogue the UI uses.
    return i18n.t('relativeTime.yesterday');
  }
  // Up to six days back a weekday is unambiguous; at seven it would repeat
  // today's weekday, so those fall through to a date.
  if (diffDays <= 6) {
    return d.toLocaleDateString(undefined, { weekday: 'short' });
  }

  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(
    undefined,
    sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' },
  );
};

/** Viewer's local clock, e.g. "6:38 PM". */
const formatTimeFromUtc = (date: Date): string => {
  const hour12 = date.getHours() % 12 || 12;
  const ampm = date.getHours() < 12 ? 'AM' : 'PM';
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hour12}:${minutes} ${ampm}`;
};

/** Viewer's local date, e.g. "Aug 14". */
const formatDateFromUtc = (date: Date): string =>
  `${MONTHS_SHORT[date.getMonth()]} ${date.getDate()}`;

/** Viewer's local date and time, e.g. "Aug 14, 6:38 PM". */
const formatDateTimeFromUtc = (date: Date): string =>
  `${formatDateFromUtc(date)}, ${formatTimeFromUtc(date)}`;

/**
 * Feed / activity created-at label. Same rules as {@link timeAgo}.
 * `naiveZone` must match how that API field is stored (see parseToDate).
 */
const formatPostCreatedAt = (
  value: Date | string | number | null | undefined,
  naiveZone: NaiveTimestampZone = 'local',
): string => {
  if (value == null || value === '') return '';
  return timeAgo(value, 'default', naiveZone);
};

/** Local instant → UTC ISO string for APIs that expect UTC. */
const convertLocalDateTimeToUtc = (date: Date): string => date.toISOString();

const formatConversationListDate = (timestamp: Date | string | number | null | undefined): string => {
  if (!timestamp) return '';
  const d = parseToDate(timestamp, 'utc');
  if (!d) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const targetDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  
  if (targetDate.getTime() === today.getTime()) {
    const hour12 = d.getHours() % 12 || 12;
    const ampm = d.getHours() < 12 ? 'AM' : 'PM';
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hour12}:${minutes} ${ampm}`;
  } else if (targetDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    return formatDate(d, 'MMM DD, YYYY');
  }
};

/** Minimum required age for onboarding and profile (years). */
const MIN_AGE_REQUIREMENT = 18;

/**
 * Returns the maximum allowable date of birth for an 18-year-old (today minus 18 years).
 */
const getMaxDateOfBirth = (minAge = MIN_AGE_REQUIREMENT): Date => {
  const today = new Date();
  return new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());
};

/**
 * Calculate completed years of age from a date of birth.
 */
const getAge = (dob: Date | string | number | null | undefined): number => {
  if (!dob) return 0;
  const d = dob instanceof Date ? dob : parseToDate(dob) ?? new Date(dob);
  if (isNaN(d.getTime())) return 0;

  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) {
    age--;
  }
  return age;
};

/**
 * Validates if the given date of birth meets the minimum age requirement (default: 18).
 */
const isAtLeastAge = (
  dob: Date | string | number | null | undefined,
  minAge = MIN_AGE_REQUIREMENT,
): boolean => {
  if (!dob) return false;
  return getAge(dob) >= minAge;
};

export type { NaiveTimestampZone };

export {
  MIN_AGE_REQUIREMENT,
  getMaxDateOfBirth,
  getAge,
  isAtLeastAge,
  getTimezoneOffsetMinutes,
  formatDate,
  parseToDate,
  formatDateOfBirth,
  toDateOnlyString,
  parseDateOnlyString,
  timeAgo,
  isToday,
  startOfDay,
  endOfDay,
  formatChatTime,
  formatConversationTime,
  formatTimeFromUtc,
  formatDateFromUtc,
  formatDateTimeFromUtc,
  formatPostCreatedAt,
  convertLocalDateTimeToUtc,
  formatConversationListDate,
};
